import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

const { FiX, FiSearch, FiClock, FiTrash2, FiCopy, FiEdit2, FiCheck } = FiIcons;

const SavedRedirectsModal = ({ isOpen, onClose, onLoad, showToast, userId }) => {
  const [redirects, setRedirects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchRedirects();
    }
  }, [isOpen]);

  const fetchRedirects = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('saved_redirects')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRedirects(data || []);
    } catch (error) {
      console.error('Error fetching redirects:', error);
      showToast('Failed to load saved redirects', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this redirect?')) return;

    try {
      const { error } = await supabase
        .from('saved_redirects')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setRedirects(redirects.filter(r => r.id !== id));
      showToast('Redirect deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting redirect:', error);
      showToast('Failed to delete redirect', 'error');
    }
  };

  const handleStartEdit = (redirect) => {
    setEditingId(redirect.id);
    setEditingName(redirect.name);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingName('');
  };

  const handleSaveEdit = async (id) => {
    if (!editingName.trim()) {
      showToast('Name cannot be empty', 'error');
      return;
    }

    try {
      const { error } = await supabase
        .from('saved_redirects')
        .update({ name: editingName.trim() })
        .eq('id', id);

      if (error) throw error;

      setRedirects(redirects.map(r => 
        r.id === id ? { ...r, name: editingName.trim() } : r
      ));
      setEditingId(null);
      setEditingName('');
      showToast('Redirect renamed successfully', 'success');
    } catch (error) {
      console.error('Error updating redirect:', error);
      showToast('Failed to rename redirect', 'error');
    }
  };

  const handleLoad = (redirect) => {
    onLoad(redirect);
    onClose();
    showToast(`Loaded: ${redirect.name}`, 'success');
  };

  const handleCopyCode = async (redirect) => {
    try {
      await navigator.clipboard.writeText(redirect.encrypted_code);
      showToast('Code copied to clipboard!', 'success');
    } catch (error) {
      showToast('Failed to copy code', 'error');
    }
  };

  const filteredRedirects = redirects.filter(redirect =>
    redirect.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    redirect.target_url.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-slate-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[80vh] overflow-hidden border border-slate-700/50"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-slate-700/50">
            <div>
              <h2 className="text-2xl font-bold text-slate-100">Saved Redirects</h2>
              <p className="text-slate-400 text-sm mt-1">
                {redirects.length} saved redirect{redirects.length !== 1 ? 's' : ''}
              </p>
            </div>
            <motion.button
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiX} className="w-6 h-6 text-slate-400" />
            </motion.button>
          </div>

          {/* Search Bar */}
          <div className="p-6 border-b border-slate-700/50">
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name or URL..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
              />
            </div>
          </div>

          {/* Redirects List */}
          <div className="overflow-y-auto max-h-[calc(80vh-240px)] p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
              </div>
            ) : filteredRedirects.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-slate-400 text-lg">
                  {searchTerm ? 'No redirects found matching your search' : 'No saved redirects yet'}
                </p>
                <p className="text-slate-500 text-sm mt-2">
                  {!searchTerm && 'Create and save your first redirect to see it here'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredRedirects.map((redirect) => (
                  <motion.div
                    key={redirect.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-slate-700/30 rounded-xl p-4 border border-slate-600/30 hover:border-blue-500/30 transition-all group"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        {editingId === redirect.id ? (
                          <div className="flex items-center gap-2 mb-2">
                            <input
                              type="text"
                              value={editingName}
                              onChange={(e) => setEditingName(e.target.value)}
                              className="flex-1 px-3 py-1.5 bg-slate-600/50 border border-slate-500/50 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveEdit(redirect.id);
                                if (e.key === 'Escape') handleCancelEdit();
                              }}
                            />
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => handleSaveEdit(redirect.id)}
                              className="p-2 bg-green-600/20 hover:bg-green-600/30 text-green-400 rounded-lg transition-colors"
                            >
                              <SafeIcon icon={FiCheck} className="w-4 h-4" />
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={handleCancelEdit}
                              className="p-2 bg-slate-600/50 hover:bg-slate-600/70 text-slate-400 rounded-lg transition-colors"
                            >
                              <SafeIcon icon={FiX} className="w-4 h-4" />
                            </motion.button>
                          </div>
                        ) : (
                          <h3 className="text-lg font-semibold text-slate-100 mb-1 truncate">
                            {redirect.name}
                          </h3>
                        )}
                        <p className="text-sm text-slate-400 truncate mb-2">
                          {redirect.target_url}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <SafeIcon icon={FiClock} className="w-3 h-3" />
                          <span>{formatDate(redirect.created_at)}</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleLoad(redirect)}
                          className="p-2 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 rounded-lg transition-colors"
                          title="Load this redirect"
                        >
                          <SafeIcon icon={FiCopy} className="w-4 h-4" />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleCopyCode(redirect)}
                          className="p-2 bg-purple-600/20 hover:bg-purple-600/30 text-purple-400 rounded-lg transition-colors"
                          title="Copy encrypted code"
                        >
                          <SafeIcon icon={FiCopy} className="w-4 h-4" />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleStartEdit(redirect)}
                          className="p-2 bg-yellow-600/20 hover:bg-yellow-600/30 text-yellow-400 rounded-lg transition-colors"
                          title="Rename"
                        >
                          <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleDelete(redirect.id)}
                          className="p-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SavedRedirectsModal;